// The Queue's company + team toggles (2026-10-07): 1A, AM and Support, any
// combination. A conversation is in view when it matches ANY selected toggle:
//
//   1A      -> company 1A on team Customer Service, or company 1A escalated
//              (any team), or a Sales Hold Phone Call (any company);
//   AM      -> company AM on team Customer Service, or company AM escalated;
//   Support -> team Support (any company), or escalated (any company), or a
//              Sales Hold Phone Call.
//
// A task contact (channel 'task', Tasks Phase 1) follows its own rule, which
// depends on WHO is looking (2026-10-14; taskMatchesToggles() below), escalated
// or not. Its company is its account's: 1A_TASKS is 1A, AM_TASKS is AM, and
// TASKS_UNROUTED (no order) is neither.
//   admin                          Support -> every task; 1A -> 1A tasks;
//                                  AM -> AM tasks
//   csr, Tasks + Support Email or  Support -> every task; 1A / AM -> none
//   Support Voice
//   csr, Tasks only                1A -> 1A tasks and unrouted ones;
//                                  AM -> AM tasks and unrouted ones
//   csr without Tasks              none (agent-visibility.js hides them too)
//
// These are FILTERS ONLY: they apply within the caller's own visibility
// (services/agent-visibility.js and the escalated / waiting veils) and never
// widen it. The server's list query (repositories/conversations.js's
// queueTogglesSql()) applies the same rule; this copy keeps the client's
// safety filter and live merge in line with it.
import { effectiveTeam } from './team.js'

export const QUEUE_TOGGLES = ['1A', 'AM', 'Support']
export const SALES_HOLD_PHONE_CALL_INTENT = 'sales_hold_phone_call'
export const TASKS_UNROUTED_ACCOUNT_CODE = 'TASKS_UNROUTED'

export const TASK_ACCOUNT_COMPANY = Object.freeze({ '1A_TASKS': '1A', 'AM_TASKS': 'AM' })

// The viewer the task rule needs: { admin, tasks, supportEmail,
// supportVoice }. A missing viewer is treated as an admin (the server's own
// visibility veils still apply on top).
export function taskMatchesToggles(conversation, selected, viewer = null) {
  const v = viewer ?? { admin: true }
  const company = TASK_ACCOUNT_COMPANY[conversation.company] ?? null
  if (v.admin) {
    return selected.has('Support') || (company != null && selected.has(company))
  }
  if (!v.tasks) return false
  if (v.supportEmail || v.supportVoice) return selected.has('Support')
  return ['1A', 'AM'].some((t) => selected.has(t) && (company === null || company === t))
}

export function conversationMatchesToggles(conversation, toggles, viewer = null) {
  if (!conversation) return false
  const selected = new Set(toggles ?? [])
  const company = conversation.companyCode ?? '1A'
  const team = conversation.effectiveTeam ?? effectiveTeam(conversation)
  const escalated = conversation.status === 'escalated'
  const salesHoldCall = (conversation.intentOverride ?? conversation.latestIntent) === SALES_HOLD_PHONE_CALL_INTENT
  if (conversation.channel === 'task') return taskMatchesToggles(conversation, selected, viewer)
  if (selected.has('1A') && ((company === '1A' && (team === 'customer_service' || escalated)) || salesHoldCall)) return true
  if (selected.has('AM') && company === 'AM' && (team === 'customer_service' || escalated)) return true
  if (selected.has('Support') && (team === 'support' || escalated || salesHoldCall)) return true
  return false
}

// The selection with unknown values and duplicates removed, in toggle order.
export function normalizeToggles(toggles) {
  const selected = new Set(toggles ?? [])
  return QUEUE_TOGGLES.filter((t) => selected.has(t))
}
